// @ts-nocheck

import { map, size } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { usePrevious } from '../../hooks';
import Formatter from '../../i18n/formatter';
import { getCurrency, __ } from '../../i18n/l10n';
import type { BasketBonusesState, BasketEligibleBoosterState, BasketEligibleBonusState } from '../../states/basket';
import theme from '../../styled-components/dark';
import { BasketModes, BasketStatuses } from '../../types/basket';
import { OddsChanges } from '../../types/basket';
import type { BasketMode } from '../../types/basket';
import { Licences } from '../../types/common';
import BasketBonusListContainer from '../BasketBonusList/BasketBonusListContainer';
import BasketBoosters from '../BasketBoosters';
import BasketOdds from '../BasketOdds';
import PresetsContainer from '../BasketPresets/BasketPresetsContainer';
import BasketStakeContainer from '../BasketStake/BasketStakeContainer';
import BasketSummaryGainDETax from '../BasketSummaryGainDETax';

const Wrapper = styled.div`
  color: #fff;
`;

const Value = styled.span`
  font-size: 1.225em;
  font-weight: bold;
  float: right;
  color: ${theme.Basket.readOnlyStakeRightColor};
`;

const TotalOdd = styled.div`
  margin-left: 2em;
  padding: 0.2em 0;
  color: ${theme.BasketSummary.totalOddColor};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

const PotentialValue = styled(Value)`
  font-size: 1.225em;
  color: ${theme.BasketCard.potentialWinningsColor};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.1em 0;
`;

const PotentialRow = styled(Row)`
  font-weight: bold;
  color: ${theme.BasketCard.potentialWinningsColor};
`;

const TotalRow = styled(Row)`
  font-weight: bold;
`;

const SubLabel = styled.span`
  color: ${theme.BasketCard.subLabelColor};
  font-size: 0.8em;
`;

const SidePadding = styled.div`
  padding: 0.2em 0.6em 0;
`;

const InnerSidePadding = styled.div`
  ${({ $withPadding, $singleElementRow }) => {
    if ($withPadding) {
      if ($singleElementRow) {
        return css`
          padding: 0.2em 0em;
        `;
      }
      return css`
        padding: 0.2em 0.5em 0.2em 0em;
      `;
    }

    return css`
      padding: 0.2em 0.5em;
    `;
  }}

  ${({ $flex }) => {
    if ($flex) {
      return css`
        display: flex;
        flex-direction: row;
        align-items: center;
      `;
    }
  }}

  ${({ $withSeparator }) => {
    if ($withSeparator) {
      return css`
        &:not(:last-child) {
          border-bottom: 1px solid #31333a;
        }
      `;
    }
  }}

  ${({ $hide }) => {
    if ($hide) {
      return css`
        position: absolute;
        opacity: 0;
        pointer-events: none;
        padding: 0;
        height: 0;
        margin: 0;
        width: 0;
        border-bottom: none;
      `;
    }
  }}
`;

const StyledStakeContainer = styled(BasketStakeContainer)`
  & {
    padding: 0.2em 0.5em;
  }
`;

type Props = {
  stake: string;
  mode: BasketMode;
  selectedFreebetId: number;
  boosterGain: number;
  bonusGain: number;
  eligibleBonuses: Array<BasketEligibleBonusState>;
  selectedBonusId: number;
  bonuses: BasketBonusesState;
  nbBet: number;
  totalCost: number;
  selectedBonusesValues: Array<BasketEligibleBonusState>;
  totalPotentialGain: number;
  minusDEGainTaxValue: number;
  totalOdd: number;
  previousTotalOdd?: number;
  eligibleBooster: BasketEligibleBoosterState;
  oddsHasChanged: boolean;
  readOnly: boolean;
  licence: string;
  status: string;
};

const BasketSummary = ({
  stake,
  mode,
  selectedFreebetId,
  boosterGain,
  bonusGain,
  eligibleBonuses,
  selectedBonusId,
  bonuses,
  nbBet,
  totalCost,
  selectedBonusesValues,
  totalPotentialGain,
  minusDEGainTaxValue,
  totalOdd,
  previousTotalOdd,
  eligibleBooster,
  oddsHasChanged,
  readOnly,
  licence,
  status,
}: Props) => {
  const [totalOddDirection, setTotalOddDirection] = useState();
  const isCombined = mode === BasketModes.COMBINED;
  const isSystem = mode === BasketModes.SYSTEM;
  const isSimple = mode === BasketModes.SIMPLE;
  const showDoubleOddCombined =
    isCombined && eligibleBonuses && size(eligibleBonuses) > 0 && !selectedFreebetId && selectedBonusId;
  const showDoubleOddSimple = isSimple && selectedBonusesValues.length > 0;
  const previousNbBet = usePrevious(nbBet);
  const bonusWrapperRef = useRef();
  const boostersWrapperRef = useRef();
  const DETaxWrapperRef = useRef();
  const sidePaddingRef = useRef();
  const [showBonus, setShowBonus] = useState(false);
  const [showBoosters, setShowBoosters] = useState(false);
  const [showDETax, setShowDETax] = useState(false);
  const [nbInnerSidePadding, setNbInnerSidePadding] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const node = bonusWrapperRef.current;

    if (node) {
      setShowBonus(!!node.childElementCount);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const node = boostersWrapperRef.current;

    if (node) {
      setShowBoosters(!!node.childElementCount);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const node = DETaxWrapperRef.current;

    if (node) {
      setShowDETax(!!node.childElementCount);
    }
  });

  useEffect(() => {
    const node = sidePaddingRef.current;

    if (node) {
      setNbInnerSidePadding(node.childElementCount - (!showBoosters ? 1 : 0) - (!showBonus ? 1 : 0));
    }
  }, [showBonus, showBoosters]);

  useEffect(() => {
    if (totalOdd && previousTotalOdd && totalOdd !== previousTotalOdd && oddsHasChanged) {
      setTotalOddDirection(Math.sign(totalOdd - previousTotalOdd) > 0 ? OddsChanges.UP : OddsChanges.DOWN);
    }
  }, [totalOdd, previousTotalOdd, oddsHasChanged]);

  useEffect(() => {
    if ((nbBet && previousNbBet && nbBet !== previousNbBet) || !oddsHasChanged) {
      setTotalOddDirection(undefined);
    }
  }, [nbBet, oddsHasChanged, previousNbBet]);

  return (
    <Wrapper>
      <SidePadding ref={sidePaddingRef}>
        {isSimple && nbBet > 1 ? (
          <InnerSidePadding>
            <TotalRow>
              {__('Total wager')}
              <Value> {Formatter.formatNumber(totalCost, getCurrency())}</Value>
            </TotalRow>
          </InnerSidePadding>
        ) : null}

        <InnerSidePadding
          $withPadding
          $singleElementRow={isSimple || isSystem || (status === BasketStatuses.VALIDATED && !isCombined)}
          $flex
          $withSeparator={!(isSimple && nbBet === 1) && !isSystem && nbInnerSidePadding > 2}
        >
          <StyledStakeContainer selectedFreebetId={selectedFreebetId} />
          {isCombined ? (
            <TotalOdd>
              {__('Total odds')}
              <BasketOdds value={totalOdd} changed={totalOddDirection} displayLabel={false} />
            </TotalOdd>
          ) : null}
        </InnerSidePadding>

        <InnerSidePadding ref={bonusWrapperRef} $hide={!showBonus} $withSeparator={nbInnerSidePadding > 2}>
          <BasketBonusListContainer />
        </InnerSidePadding>

        {isSystem ? (
          <InnerSidePadding $withSeparator={nbInnerSidePadding > 2}>
            <TotalRow>
              {__('Total wager')}
              <Value>
                {' '}
                <SubLabel>
                  {nbBet} x {Formatter.formatNumber(parseFloat(stake), getCurrency())} ={' '}
                </SubLabel>
                {Formatter.formatNumber(totalCost, getCurrency())}
              </Value>
            </TotalRow>
          </InnerSidePadding>
        ) : null}

        <InnerSidePadding ref={boostersWrapperRef} $hide={!showBoosters} $withSeparator={nbInnerSidePadding > 2}>
          <BasketBoosters
            readOnly={readOnly}
            isCombined={isCombined}
            isSystem={isSystem}
            eligibleBooster={eligibleBooster}
            boosterGain={boosterGain}
          />
        </InnerSidePadding>

        {/* ----------------- BONUS DOUBLE ODD SIMPLE BETS --------------- */}
        {showDoubleOddSimple ? (
          <InnerSidePadding $withSeparator={nbInnerSidePadding > 2}>
            {selectedBonusesValues.map((bonus) => (
              <Row key={bonus.promotionId}>
                <span>{__('Winnings %1$s', bonus.title)}</span>
                <Value>{Formatter.formatNumber(bonus.possibleBonus, getCurrency())}</Value>
              </Row>
            ))}
          </InnerSidePadding>
        ) : null}

        {/* --------------- BONUS DOUBLE ODD AMOUNT FOR COMBINED BETS ----------------- */}
        {showDoubleOddCombined ? (
          <InnerSidePadding $withSeparator={nbInnerSidePadding > 2}>
            {map(eligibleBonuses, ({ promotionId }) => {
              const bonus = bonuses[promotionId];
              const selected = promotionId === selectedBonusId;

              return selected ? (
                <Row key={promotionId}>
                  <span>{__('Winnings %1$s', bonus.title)}</span>
                  <Value>{Formatter.formatNumber(bonusGain, getCurrency())}</Value>
                </Row>
              ) : null;
            })}
          </InnerSidePadding>
        ) : null}
        <InnerSidePadding ref={DETaxWrapperRef} $hide={!showDETax} $withSeparator={nbInnerSidePadding > 2}>
          {licence === Licences.DE ? <BasketSummaryGainDETax minusDEGainTaxValue={minusDEGainTaxValue} /> : null}
        </InnerSidePadding>
        <InnerSidePadding $withSeparator={nbInnerSidePadding > 2}>
          <PotentialRow>
            {__('Potential winnings')}
            <PotentialValue>{Formatter.formatNumber(totalPotentialGain, getCurrency())}</PotentialValue>
          </PotentialRow>
        </InnerSidePadding>
      </SidePadding>
      <PresetsContainer odds={totalOdd} selectedFreebetId={selectedFreebetId} />
    </Wrapper>
  );
};

export default BasketSummary;
